import add from 'date-fns/add'
import format from 'date-fns/format'
import isToday from 'date-fns/isToday'
import subtract from 'date-fns/sub'
import React from 'react'

import { useAppDispatch } from '@flecto-io/core'
import getScheduleEvents from '@flecto-io/core/src/actions/schedule/getEvents'
import { useQueryGetAllBookings } from '@flecto/core/saas'
import { i18n } from '@flecto/translations'
import { type UNSAFE_any } from '@flecto/types'
import { formatDateString } from '@flecto/utils'

export const useBookingCalendar = () => {
  const LIMIT_BOOKINGS = 100
  const dispatch = useAppDispatch()
  const [datesSchedule, setDatesSchedule] = React.useState<Date[] | null>(null)

  const [calendar, setCalendar] = React.useState(new Map())

  const { data: bookings, isLoading: isBookingsLoading } =
    useQueryGetAllBookings()

  const scrollToTop = () => {
    const element = document.getElementsByClassName('key-calendar-0')

    if (element && element.length > 0) {
      element[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  React.useLayoutEffect(() => {
    handleTodayWeek()
  }, [])

  React.useEffect(() => {
    updateCurrentWeekLanguageLabel()
  }, [i18n.language])

  React.useEffect(() => {
    try {
      if (datesSchedule?.length) {
        loadWeekData(datesSchedule[0], datesSchedule[1])
      }
    } catch (e) {
      if (e instanceof Error) {
        console.error(e.message)
      }
    }
  }, [bookings, datesSchedule])

  const updateCurrentWeekLanguageLabel = React.useCallback(() => {
    const newCalendar = new Map()

    calendar.forEach((item, key) => {
      const currentitem = { ...item }
      const startDate = new Date(item?.currentDateStart)
      const newLabel = formatDateString({
        date: String(startDate),
        variant: 'week-full',
      })

      currentitem.label = newLabel

      newCalendar.set(key, currentitem)
    })

    if (newCalendar.size > 0) {
      setCalendar(newCalendar)
    }
  }, [calendar, i18n.language])

  const loadWeekData = (startDate: Date, endDate: Date) => {
    const currentCalendar = new Map()
    for (let index = 0; startDate.getTime() < endDate.getTime(); index++) {
      const currentDay = format(startDate.getTime(), 'yyyy-MM-dd')
      const currentDateStart = `${currentDay}T00:00:00`
      const currentDateEnd = `${currentDay}T23:59:59`

      const filteredRows = bookings?.booking.filter((booking: UNSAFE_any) => {
        const booking_start_date = format(
          new Date(booking.start_at),
          'yyyy-MM-dd'
        )
        const booking_end_date = format(new Date(booking.end_at), 'yyyy-MM-dd')
        const formatedCurrentDateStart = format(
          new Date(currentDateStart),
          'yyyy-MM-dd'
        )
        const formatedCurrentDateEnd = format(
          new Date(currentDateEnd),
          'yyyy-MM-dd'
        )

        return (
          booking_start_date === formatedCurrentDateStart ||
          booking_end_date === formatedCurrentDateEnd
        )
      })

      currentCalendar.set(String(index), {
        label: formatDateString({
          date: String(startDate),
          variant: 'week-full',
        }),
        currentDateStart,
        currentDateEnd,
        key: index,
        containerId: `container--date-${currentDay}`,
        isToday: isToday(startDate),
        rows: filteredRows,
      })
      startDate = add(startDate, {
        days: 1,
      })
    }

    setCalendar(currentCalendar)
  }

  const handleTodayWeek = () => {
    const startDate = subtract(new Date(), {
      days: 3,
    })
    const endDate = add(new Date(), {
      days: 4,
    })

    setDatesSchedule([startDate, endDate])
    const start_at = format(startDate.getTime(), 'yyyy-MM-dd')
    const end_at = format(endDate.getTime(), 'yyyy-MM-dd')

    dispatch(
      getScheduleEvents({
        start_at,
        end_at,
        limit: LIMIT_BOOKINGS,
      })
    )
  }

  const handlePreviousWeek = () => {
    const hasDate = calendar.has('0')

    if (hasDate) {
      const date = new Date(calendar.get('0').currentDateStart)
      const startDate = subtract(date, {
        days: 7,
      })

      const endDate = subtract(date, {
        days: 0,
      })

      const start_at = format(startDate.getTime(), 'yyyy-MM-dd')
      const end_at = format(endDate.getTime(), 'yyyy-MM-dd')

      setDatesSchedule([startDate, endDate])

      dispatch(
        getScheduleEvents({
          start_at,
          end_at,
          limit: LIMIT_BOOKINGS,
        })
      )
    }
  }

  const handleNextWeek = React.useCallback(() => {
    const hasDate = calendar.has(String(calendar.size - 1))

    if (hasDate) {
      const calendarSelectNext = calendar.get(String(calendar.size - 1))

      const date = new Date(calendarSelectNext.currentDateStart)
      const startDate = add(date, {
        days: 1,
      })

      const endDate = add(date, {
        days: 8,
      })

      const start_at = format(startDate.getTime(), 'yyyy-MM-dd')
      const end_at = format(endDate.getTime(), 'yyyy-MM-dd')

      setDatesSchedule([startDate, endDate])

      dispatch(
        getScheduleEvents({
          start_at,
          end_at,
          limit: LIMIT_BOOKINGS,
        })
      )

      loadWeekData(startDate, endDate)
      scrollToTop()
    }
  }, [calendar])

  return {
    calendar,
    handlePreviousWeek,
    handleTodayWeek,
    handleNextWeek,
    isBookingsLoading,
  }
}
